import React from "react";
import { useQuery } from "@tanstack/react-query";
import { FaTrashAlt } from "react-icons/fa";

import { PaziaInlineLoader } from "components/atoms";
import { TRANCHE_POLICY_TYPE } from "consts";
import { CourseTranchePriceApi, coursetranchesApi } from "api";
import { QUERIES_KEYS } from "queries";
import { useToaster } from "utils";
import { TokpCalendar } from "./TokpCalendar";
import { PaziaBox } from "components/atoms";

export interface PricesPreviewProps {
  courseId: number;
  courseEnd: Date;
}
export const PricesPreview = ({ courseId, courseEnd }: PricesPreviewProps) => {
  const { showError, showSuccess } = useToaster();
  const { isLoading, data, refetch } = useQuery({
    queryKey: QUERIES_KEYS.COURSE_TRANCHES_PRICES(courseId),
    queryFn: () => coursetranchesApi.fetchCoursePolicies(courseId),
  });

  if (!data?.data) return null;

  const { standardPrice, highestPrice, lowestPrice, policies } = data.data;
  const onPolicyDelete = async (policyId: number) => {
    try {
      await coursetranchesApi.deletePolicy(courseId, policyId);
      showSuccess("Pomyślnie usunięto politykę cenową");
      refetch();
    } catch (error) {
      console.error(error);
      showError("Nie udało się usunąć polityki cenowej");
    }
  };
  const defaultActiveMonth =
    courseEnd > new Date()
      ? { year: new Date().getFullYear(), month: new Date().getMonth() }
      : { year: courseEnd.getFullYear(), month: courseEnd.getMonth() };

  return (
    <div className="flex flex-col gap-2">
      <PaziaBox>
        {isLoading ? (
          <div className="flex justify-center">
            <div>
              <PaziaInlineLoader />
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between">
              <div className="text-md font-semibold dark:text-slate-200">Standardowa cena</div>
              <div className="text-md font-semibold">{standardPrice} zł</div>
            </div>
            <div className="flex justify-between text-sm">
              <div className="">Najwyższa cena</div>
              <div className="text-red-500">{highestPrice} zł</div>
            </div>
            <div className="flex justify-between text-sm">
              <div className="">Najniższa cena</div>
              <div className="text-green-500">{lowestPrice} zł</div>
            </div>
            {policies.length > 0 && (
              <div>
                <div className="text-md mt-2 font-semibold dark:text-slate-200">Zastosowane polityki zmiany ceny</div>
                {policies.map((x) => (
                  <div
                    key={x.id}
                    className="-mx-2 flex justify-between px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    <TranchePolicyPreview key={x.id} policy={x} />
                    <FaTrashAlt
                      onClick={() => {
                        onPolicyDelete(x.id);
                      }}
                      size={16}
                      className="cursor-pointer text-slate-400 hover:text-black"
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </PaziaBox>
      <div className="flex justify-between">
        <TokpCalendar
          startDate={new Date(2023, 7, 1)}
          endDate={courseEnd}
          defaultActiveMonth={defaultActiveMonth}
          rangesLabels={data.data.ranges.map((x) => ({
            startDate: new Date(x.startDate),
            endDate: new Date(x.endDate),
            label: x.adjustedPrice.price.toString() + " zł",
            labelColor:
              x.adjustedPrice.price === data.data.standardPrice
                ? "slate"
                : x.adjustedPrice.price < data.data.standardPrice
                  ? "green"
                  : "red",
          }))}
        />
      </div>
    </div>
  );
};

const TranchePolicyPreview = ({ policy }: { policy: CourseTranchePriceApi }) => {
  if (policy.type === TRANCHE_POLICY_TYPE.SubtractAmount)
    return (
      <div className="text-sm">
        Zmniejsz cenę transzy <span className="font-semibold">{policy.tranche.name}</span> o{" "}
        <span className="text-red-500">{policy.amount} zł</span> od{" "}
        <span className="font-semibold">{new Date(policy.from).toLocaleDateString("pl-PL")}</span>
        {policy.to && (
          <>
            {" "}
            do <span className="font-semibold">{new Date(policy.to).toLocaleDateString("pl-PL")}</span>
          </>
        )}
      </div>
    );
  if (policy.type === TRANCHE_POLICY_TYPE.AddAmount)
    return (
      <div className="text-sm">
        Zwiększ cenę transzy <span className="font-semibold">{policy.tranche.name}</span> o{" "}
        <span className="text-green-500">{policy.amount} zł</span> od{" "}
        <span className="font-semibold">{new Date(policy.from).toLocaleDateString("pl-PL")}</span>
        {policy.to && (
          <>
            {" "}
            do <span className="font-semibold">{new Date(policy.to).toLocaleDateString("pl-PL")}</span>
          </>
        )}
      </div>
    );
  if (policy.type === TRANCHE_POLICY_TYPE.SubtractPercentage)
    return (
      <div className="text-sm">
        Zmniejsz cenę transzy <span className="font-semibold">{policy.tranche.name}</span> o{" "}
        <span className="text-red-500">{policy.amount}%</span> od{" "}
        <span className="font-semibold">{new Date(policy.from).toLocaleDateString("pl-PL")}</span>
        {policy.to && (
          <>
            {" "}
            do <span className="font-semibold">{new Date(policy.to).toLocaleDateString("pl-PL")}</span>
          </>
        )}
      </div>
    );
  if (policy.type === TRANCHE_POLICY_TYPE.AddPercentage)
    return (
      <div className="text-sm">
        Zwiększ cenę transzy <span className="font-semibold">{policy.tranche.name}</span> o{" "}
        <span className="text-green-500">{policy.amount}%</span> od{" "}
        <span className="font-semibold">{new Date(policy.from).toLocaleDateString("pl-PL")}</span>
        {policy.to && (
          <>
            {" "}
            do <span className="font-semibold">{new Date(policy.to).toLocaleDateString("pl-PL")}</span>
          </>
        )}
      </div>
    );
};
